<template>
  <div>
    <div class="row mt-4 align-items-end">
      <div class="col-md-3">
        <label for="financeiro-month">Período</label>
        <input id="financeiro-month" v-model="month" type="month" class="form-control" @change="onPeriodChange">
      </div>

      <div class="col-md-9 text-right">
        <button class="btn btn-primary" :disabled="exporting" @click="exportZip">
          {{ exporting ? 'Solicitando...' : 'Exportar relatório modelo (ZIP)' }}
        </button>
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-md-4">
        <div class="card card-kpi">
          <div class="card-body">
            <small class="text-muted">Consultas</small>
            <h3>{{ summaryValue('consultas') }}</h3>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card card-kpi">
          <div class="card-body">
            <small class="text-muted">Itens</small>
            <div class="kpi-detail">
              <span>Boleto: <strong>{{ itens('Boleto') }}</strong></span>
              <span>Documento: <strong>{{ itens('Documento') }}</strong></span>
              <span>Multa: <strong>{{ itens('Multa') }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card card-kpi">
          <div class="card-body">
            <small class="text-muted">Boletos</small>
            <h3>{{ summaryValue('boletos') }}</h3>
          </div>
        </div>
      </div>
    </div>

    <hr>

    <div class="row">
      <div class="col-md-12">
        <div class="tab-bar">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="tab-btn"
            :class="{ 'tab-btn-active': activeTab === tab.key }"
            @click="changeTab(tab.key)"
          >
            {{ tab.name }}
          </button>
        </div>
      </div>
    </div>

    <div class="row mt-4 align-items-end">
      <div class="col-md-4">
        <label for="financeiro-generic">Conta (nome)</label>
        <input
          id="financeiro-generic"
          v-model="filters.generic"
          class="form-control"
          placeholder="Buscar por nome da conta"
          @keypress.enter="applyFilters"
        >
      </div>

      <div v-if="activeTab === 'orders'" class="col-md-2">
        <label for="financeiro-app">App</label>
        <select id="financeiro-app" v-model="filters.app" class="form-control">
          <option v-for="option in appOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </div>

      <div v-if="activeTab === 'orders'" class="col-md-2">
        <label for="financeiro-service">Serviço</label>
        <input id="financeiro-service" v-model="filters.service" class="form-control" @keypress.enter="applyFilters">
      </div>

      <div v-if="activeTab === 'orders'" class="col-md-2">
        <label for="financeiro-status">Status</label>
        <select id="financeiro-status" v-model="filters.status" class="form-control">
          <option v-for="option in statusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </div>

      <div v-if="activeTab === 'items'" class="col-md-3">
        <label for="financeiro-tipo">Tipo</label>
        <select id="financeiro-tipo" v-model="filters.tipo" class="form-control">
          <option v-for="option in tipoOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
        </select>
      </div>

      <div v-if="activeTab === 'boletos' || activeTab === 'boletos_detalhado'" class="col-md-3">
        <label for="financeiro-cnpj">CNPJ</label>
        <input id="financeiro-cnpj" v-model="filters.cnpj" class="form-control" @keypress.enter="applyFilters">
      </div>

      <div class="col-md-3">
        <button class="btn btn-outline mr-1" @click="applyFilters">Aplicar filtros</button>
        <button class="btn btn-outline" :disabled="rows.length === 0" @click="exportCsv">Exportar CSV</button>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-md-12">
        <strong>{{ infoTotal }}</strong>
      </div>
    </div>

    <div class="row flex-fill d-flex justify-content-start">
      <div class="col-md-12 mt-2">
        <table class="table table-borderless table-dfe">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.label">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td :colspan="columns.length" class="text-center text-muted py-4">Carregando...</td>
            </tr>
            <tr v-else-if="rows.length === 0">
              <td :colspan="columns.length" class="text-center text-muted py-4">Nenhum registro para o período e filtros.</td>
            </tr>
            <tr v-for="(row, index) in rows" v-else :key="index">
              <td v-for="column in columns" :key="column.label">
                <a v-if="column.link && column.get(row)" :href="column.get(row)" target="_blank" rel="noopener">Baixar</a>
                <template v-else>{{ column.get(row) }}</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="col-md-12 my-1">
        <Pagination
          v-model="meta.current_page"
          :total-count="meta.total_count"
          :total-pages="meta.total_pages"
          @pagination="pagination"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Pagination from '@/components/shares/specialTag/Pagination'

const PER_PAGE = 50

export default {
  components: { Pagination },
  computed: {
    columns() {
      return this.columnsByTab[this.activeTab] || []
    },
    infoTotal() {
      const total = this.meta.total_count || 0
      return `${total} ${total === 1 ? 'registro' : 'registros'}`
    }
  },
  created() {
    this.setupColumns()
    this.month = this.previousMonth()
    this.reload()
  },
  data: () => ({
    activeTab: 'orders',
    appOptions: [
      { label: 'Todos', value: '' },
      { label: 'CM-Embarcador', value: 'cm-embarcador' },
      { label: 'CM-Frotas', value: 'cm-frotas' },
      { label: 'CM-Taxas', value: 'cm-taxas' },
      { label: 'CM-Crono', value: 'cm-crono' }
    ],
    exporting: false,
    filters: { app: '', cnpj: '', generic: '', service: '', status: '', tipo: '' },
    loading: false,
    meta: { current_page: 1, total_count: 0, total_pages: 0 },
    month: '',
    page: 1,
    rows: [],
    statusOptions: [
      { label: 'Todos', value: '' },
      { label: 'Sucesso (200)', value: '200' },
      { label: 'Erro (400)', value: '400' }
    ],
    summary: null,
    tabs: [
      { key: 'orders', name: 'Consultas' },
      { key: 'items', name: 'Itens' },
      { key: 'boletos', name: 'Boletos' },
      { key: 'boletos_detalhado', name: 'Boletos detalhado' }
    ],
    tipoOptions: [
      { label: 'Todos', value: '' },
      { label: 'Boleto', value: 'Boleto' },
      { label: 'Documento', value: 'Documento' },
      { label: 'Multa', value: 'Multa' }
    ]
  }),
  methods: {
    account(row) {
      return `${row.account_id} · ${row.account_name}`
    },
    applyFilters() {
      this.page = 1
      this.loadTab()
    },
    changeTab(key) {
      if (this.activeTab === key) return

      this.activeTab = key
      this.page = 1
      this.filters = { ...this.filters, app: '', cnpj: '', service: '', status: '', tipo: '' }
      this.loadTab()
    },
    exportCsv() {
      const params = { ...this.buildParams(), format: 'csv' }
      delete params.page
      delete params.per_page

      this.axios.get(`admin/financeiro/${this.activeTab}`, { params, responseType: 'blob' })
        .then(blob => this.$parse.buildDownload(`${this.activeTab}_${this.month}.csv`, blob))
        .catch(error => this.$notify.addError(error))
    },
    exportZip() {
      this.exporting = true
      this.axios.get('admin/financeiro/export', { params: { month: this.month } })
        .then(body => this.$notify.add((body.meta && body.meta.notice) || 'Relatório em processamento. Você receberá um email com o link.'))
        .catch(error => this.$notify.addError(error))
        .finally(() => { this.exporting = false })
    },
    buildParams() {
      const params = { month: this.month, page: this.page, per_page: PER_PAGE }
      const { app, cnpj, generic, service, status, tipo } = this.filters

      if (generic) params.generic = generic
      if (this.activeTab === 'orders') {
        if (app) params.app = app
        if (service) params.service = service
        if (status) params.status = status
      }
      if (this.activeTab === 'items' && tipo) params.tipo = tipo
      if ((this.activeTab === 'boletos' || this.activeTab === 'boletos_detalhado') && cnpj) params.cnpj = cnpj

      return params
    },
    formatCnpj(value) {
      if (!value) return ''

      const digits = String(value).replace(/\D/g, '')
      if (digits.length !== 14) return value

      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    },
    formatDate(value) {
      if (!value) return ''

      const [year, month, day] = String(value).split('-')
      if (!day) return value

      return `${day}/${month}/${year}`
    },
    formatMoney(cents) {
      const value = Number(cents || 0) / 100
      return value.toLocaleString('pt-BR', { currency: 'BRL', style: 'currency' })
    },
    itens(kind) {
      const map = { Boleto: 'itens_boleto', Documento: 'itens_documento', Multa: 'itens_multa' }
      return (this.summary && this.summary[map[kind]]) || 0
    },
    loadSummary() {
      this.axios.get('admin/financeiro/summary', { params: { month: this.month } })
        .then(body => { this.summary = body.data })
        .catch(error => this.$notify.addError(error))
    },
    loadTab() {
      this.loading = true
      this.axios.get(`admin/financeiro/${this.activeTab}`, { params: this.buildParams() })
        .then(body => {
          this.rows = body.data || []
          this.meta = body.meta || { current_page: 1, total_count: 0, total_pages: 0 }
        })
        .catch(error => this.$notify.addError(error))
        .finally(() => { this.loading = false })
    },
    onPeriodChange() {
      if (!this.month) return

      this.page = 1
      this.reload()
    },
    pagination(page) {
      this.page = page
      this.loadTab()
    },
    previousMonth() {
      const now = new Date()
      const date = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    },
    reload() {
      this.loadSummary()
      this.loadTab()
    },
    summaryValue(key) {
      return (this.summary && this.summary[key]) || 0
    },
    setupColumns() {
      this.columnsByTab = {
        orders: [
          { label: 'Conta', get: row => this.account(row) },
          { label: 'Período', get: row => row.period },
          { label: 'App', get: row => row.app },
          { label: 'Serviço', get: row => row.service },
          { label: 'CNPJ', get: row => this.formatCnpj(row.cnpj) },
          { label: 'Status', get: row => row.status },
          { label: 'Quantidade', get: row => row.quantity }
        ],
        items: [
          { label: 'Conta', get: row => this.account(row) },
          { label: 'Período', get: row => row.period },
          { label: 'Tipo', get: row => row.tipo },
          { label: 'Quantidade', get: row => row.quantity }
        ],
        boletos: [
          { label: 'Conta', get: row => this.account(row) },
          { label: 'Período', get: row => row.period },
          { label: 'AIT', get: row => row.ait },
          { label: 'CNPJ', get: row => this.formatCnpj(row.cnpj) },
          { label: 'Quantidade', get: row => row.quantity }
        ],
        boletos_detalhado: [
          { label: 'Conta', get: row => this.account(row) },
          { label: 'Período', get: row => row.period },
          { label: 'AIT', get: row => row.ait },
          { label: 'CNPJ', get: row => this.formatCnpj(row.cnpj) },
          { label: 'Código de barra', get: row => row.barcode },
          { label: 'Valor', get: row => this.formatMoney(row.amount_cents) },
          { label: 'Vencimento', get: row => this.formatDate(row.due_date) },
          { label: 'Boleto', get: row => row.download_link, link: true },
          { label: 'Quantidade', get: row => row.quantity }
        ]
      }
    }
  },
  name: 'AdminFinanceiroIndex'
}
</script>

<style scoped>
  .card-kpi {
    border: 1px solid var(--border-color, #e6e6e6);
    border-radius: 6px;
  }
  .card-kpi h3 {
    margin: 4px 0 0;
    font-weight: 700;
  }
  .kpi-detail {
    display: flex;
    flex-direction: column;
    font-size: 0.85rem;
    margin-top: 4px;
  }
  .tab-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    border-bottom: 1px solid var(--border-color, #e6e6e6);
  }
  .tab-btn {
    background: transparent;
    border: none;
    padding: 8px 16px;
    font-weight: 600;
    color: #6c757d;
    cursor: pointer;
    border-bottom: 3px solid transparent;
  }
  .tab-btn-active {
    color: #318BE3;
    border-bottom-color: #318BE3;
  }
</style>
